import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { PanelSubscription } from '@storeo/api-client';
import { MSym } from '@storeo/ui';
import { api } from '../lib/api';
import { money, dateShort } from '../lib/format';
import { DemoBanner, PlaceholderTag } from '../components/lumina/Placeholder';

const SUB_STATUS: Record<string, { label: string; cls: string }> = {
  active: { label: 'Ativa', cls: 'bg-secondary-container/25 text-secondary' },
  past_due: { label: 'Pagamento atrasado', cls: 'bg-[#f59e0b]/15 text-[#f59e0b]' },
  expired: { label: 'Expirada', cls: 'bg-surface-variant text-outline' },
  canceled: { label: 'Cancelada', cls: 'bg-surface-variant text-outline' },
};

/** Clientes & LTV — redesign Lumina (docs/prototype/storeo_clientes_gest_o_de_ltv).
 *  Dado real: a lista de assinantes (subscriptions + cancelSubscription). Matriz RFM,
 *  score de retenção, jornada e insight de IA = mockup — sem backend. */
export function Clientes() {
  const qc = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);
  const subs = useQuery({ queryKey: ['subscriptions'], queryFn: () => api.subscriptions() });

  const cancel = useMutation({
    mutationFn: (s: PanelSubscription) => api.cancelSubscription(s.id, 'cancelado pelo parceiro'),
    onMutate: (s) => setBusyId(s.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscriptions'] }),
    onSettled: () => setBusyId(null),
  });

  const active = (subs.data ?? []).filter((s) => s.status === 'active');
  const mrr = active.reduce((sum, s) => sum + s.amount, 0);

  return (
    <div className="lumina flex flex-col gap-space-lg text-on-surface">
      <div className="flex flex-col gap-space-3xs">
        <div className="flex items-center gap-space-xs">
          <span className="font-label-sm text-label-sm uppercase tracking-wider text-primary font-bold">Inteligência RFM &amp; Retenção</span>
          <span className="text-outline text-label-sm">•</span>
          <span className="font-label-sm text-label-sm text-secondary flex items-center gap-space-3xs">
            <span className="w-1.5 h-1.5 rounded-full bg-secondary" /> Tempo real
          </span>
        </div>
        <h1 className="font-headline-lg text-headline-lg text-on-surface tracking-tight">Clientes &amp; LTV</h1>
        <p className="font-body-md text-body-md text-on-surface-variant">
          Coortes, lifetime value, assinaturas e segmentação de alta conversão.
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-space-md">
        <div className="p-space-lg rounded-2xl bg-surface-container-low shadow-md flex flex-col gap-space-xs">
          <span className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider">Assinantes ativos</span>
          <span className="font-metric-stat text-metric-stat text-white font-extrabold">{active.length}</span>
          <span className="font-body-sm text-body-sm text-on-surface-variant">com cobrança recorrente em dia</span>
        </div>
        <div className="p-space-lg rounded-2xl bg-surface-container-low shadow-md flex flex-col gap-space-xs">
          <span className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider">Receita recorrente (MRR)</span>
          <span className="font-metric-stat text-metric-stat text-secondary font-extrabold">R$ {money(mrr).replace('R$', '').trim()}</span>
          <span className="font-body-sm text-body-sm text-on-surface-variant">soma das assinaturas ativas</span>
        </div>
        <div className="p-space-lg rounded-2xl bg-surface-container-low shadow-md flex flex-col gap-space-xs">
          <span className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider flex items-center">
            LTV médio por comprador <PlaceholderTag />
          </span>
          <span className="font-metric-stat text-metric-stat text-white font-extrabold">R$ 342,80</span>
          <span className="font-body-sm text-body-sm text-on-surface-variant">+8,48 vs. 2025</span>
        </div>
        <div className="p-space-lg rounded-2xl bg-surface-container-low shadow-md flex flex-col gap-space-xs">
          <span className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider flex items-center">
            Churn / inatividade <PlaceholderTag />
          </span>
          <span className="font-metric-stat text-metric-stat text-white font-extrabold">6.1%</span>
          <span className="font-body-sm text-body-sm text-on-surface-variant">benchmark Top 1%</span>
        </div>
      </div>

      {/* real subscriptions table */}
      <div className="flex flex-col gap-space-md">
        <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold">Assinaturas</h2>
        <div className="rounded-2xl bg-surface-container overflow-hidden shadow-lg">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse font-body-sm text-body-sm">
              <thead>
                <tr className="bg-surface-container-highest/40 text-on-surface font-label-sm text-label-sm uppercase tracking-wider font-semibold">
                  <th className="py-space-md px-space-lg">Cliente</th>
                  <th className="py-space-md px-space-md">Produto</th>
                  <th className="py-space-md px-space-md text-right">Valor</th>
                  <th className="py-space-md px-space-md">Meio</th>
                  <th className="py-space-md px-space-md">Status</th>
                  <th className="py-space-md px-space-md">Próxima cobrança</th>
                  <th className="py-space-md px-space-lg text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="text-on-surface">
                {!subs.data?.length ? (
                  <tr>
                    <td colSpan={7} className="py-space-2xl text-center text-on-surface-variant">Nenhum assinante ainda.</td>
                  </tr>
                ) : (
                  subs.data.map((s) => (
                    <tr key={s.id} className="hover:bg-surface-container-high/60 transition-colors border-t border-surface-container-highest/30">
                      <td className="py-space-md px-space-lg">
                        <span className="font-label-md text-label-md font-semibold text-on-surface block truncate max-w-[14rem]">
                          {s.customerName || s.customerEmail}
                        </span>
                        <span className="text-outline font-label-sm text-label-sm block truncate max-w-[14rem]">{s.customerEmail}</span>
                      </td>
                      <td className="py-space-md px-space-md text-on-surface-variant truncate max-w-[12rem]">{s.productName}</td>
                      <td className="py-space-md px-space-md text-right font-label-md text-label-md font-bold text-on-surface whitespace-nowrap">
                        {money(s.amount)}/mês
                      </td>
                      <td className="py-space-md px-space-md font-label-sm text-label-sm text-on-surface-variant">
                        {s.paymentMethod === 'card' ? 'CARTÃO' : 'PIX'}
                      </td>
                      <td className="py-space-md px-space-md">
                        <span className={`px-2 py-0.5 rounded-full font-label-sm text-label-sm font-bold ${SUB_STATUS[s.status]?.cls ?? 'bg-surface-variant text-outline'}`}>
                          {SUB_STATUS[s.status]?.label ?? s.status}
                        </span>
                      </td>
                      <td className="py-space-md px-space-md text-outline">{s.status === 'active' ? dateShort(s.nextChargeAt) : '—'}</td>
                      <td className="py-space-md px-space-lg text-right">
                        {s.status === 'active' ? (
                          <button
                            className="px-space-sm py-1 rounded-lg bg-surface-container-high hover:bg-error/15 hover:text-error text-on-surface font-label-sm text-label-sm font-semibold transition-colors disabled:opacity-50"
                            disabled={busyId === s.id}
                            onClick={() => cancel.mutate(s)}
                          >
                            Cancelar
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <DemoBanner>As seções abaixo (segmentos RFM, jornada e insight de IA) são prévia do design — ainda sem dados reais.</DemoBanner>

      {/* RFM + journey — mock */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-space-md">
        <div className="lg:col-span-7 rounded-2xl bg-surface-container-low p-space-lg shadow-md">
          <h3 className="font-headline-sm text-headline-sm text-on-surface font-bold mb-space-md">Segmentos RFM</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-sm">
            {[
              { label: 'Campeões', v: '18%', c: 'text-secondary', d: 'Compram muito e há pouco tempo' },
              { label: 'Fiéis', v: '24%', c: 'text-primary', d: 'Recompra frequente' },
              { label: 'Em risco', v: '12%', c: 'text-[#f59e0b]', d: 'Sumiram há 60+ dias' },
              { label: 'Novos', v: '31%', c: 'text-tertiary', d: 'Primeira compra recente' },
            ].map((seg) => (
              <div key={seg.label} className="p-space-md rounded-xl bg-surface-container flex flex-col gap-space-3xs">
                <span className={`font-headline-sm text-headline-sm font-bold ${seg.c}`}>{seg.v}</span>
                <span className="font-label-md text-label-md text-on-surface font-semibold">{seg.label}</span>
                <span className="font-body-sm text-body-sm text-on-surface-variant">{seg.d}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5 rounded-2xl bg-surface-container-low p-space-lg shadow-md flex flex-col gap-space-sm">
          <div className="flex items-center gap-space-xs">
            <MSym name="auto_awesome" className="text-primary text-[20px]" />
            <h3 className="font-headline-sm text-headline-sm text-on-surface font-bold">Insight da IA</h3>
          </div>
          <p className="font-body-md text-body-md text-on-surface-variant">
            Clientes que compraram infoproduto têm 3,2× mais chance de assinar. Um cupom de boas-vindas na entrega
            costuma converter esse grupo.
          </p>
          <button className="self-start px-space-md py-space-xs rounded-xl bg-primary/10 text-primary font-label-sm text-label-sm font-bold">
            Ligar automação recomendada
          </button>
        </div>
      </div>
    </div>
  );
}
